import $t from '../i18n'
import { getAge } from './familyUtils'
import {
  filterHTNAppointments,
  filterMHAppointments,
  filterHTNFollowUpAppointments,
  filterHTNFollowUpAppointmentsInNextWeek,
  filterDiabetesAppointments,
  filterDiabetesFollowUpAppointments,
  filterDiabetesFollowUpAppointmentsInNextWeek,
} from './appointmentsUtils'
import {
  clinicActions,
  clinicTypes,
  HMOIntentOptions,
  HMOService,
  Origin,
  Variant,
} from '../consts'
import { getIsHmoMember } from './hmoUtils'

export const isOverThirty = state => {
  const age = getAge(state.profile.userDetails.dateOfBirth)

  return age > 30
}

export const isFollowUp = (clinic, antidoteClinics) => {
  return antidoteClinics?.includes(clinic)
}

export const isInClinic = (state, clinic) => {
  const antidoteClinics = state.account?.clinicsData?.antidoteClinics
  return antidoteClinics?.includes(clinic)
}

export const isReferredToClinic = (state, clinic) => {
  const clinicInvitations = state.account?.clinicsData?.clinicInvitations
  return clinicInvitations?.includes(clinic)
}

export const hasHTNAppointment = state => {
  return filterHTNAppointments(state.appointments.appointments).length > 0
}

export const hasMHAppointment = state => {
  return filterMHAppointments(state.appointments.appointments).length > 0
}

export const hasDiabetesAppointment = state => {
  return filterDiabetesAppointments(state.appointments.appointments).length > 0
}

export const hasDiabetesFollowUpAppointment = state => {
  return (
    filterDiabetesFollowUpAppointments(state.appointments.appointments).length >
    0
  )
}

export const hasHTNFollowUpAppointment = state => {
  return (
    filterHTNFollowUpAppointments(state.appointments.appointments).length > 0
  )
}

export const hasHTNFollowUpAppointmentInNextWeek = state => {
  return (
    filterHTNFollowUpAppointmentsInNextWeek(state.appointments.appointments)
      .length > 0
  )
}
export const hasDiabetesFollowUpAppointmentInNextWeek = state => {
  return (
    filterDiabetesFollowUpAppointmentsInNextWeek(
      state.appointments.appointments
    ).length > 0
  )
}

export const htnSideMenuDisplay = state => {
  if (hasHTNFollowUpAppointmentInNextWeek(state)) {
    return $t('sideMenu.followUpIntake')
  } else if (hasHTNAppointment(state)) {
    return $t('sideMenu.clinics.nextAppointments')
  } else if (isReferredToClinic(state, clinicTypes.HTN)) {
    return $t('sideMenu.clinics.referral')
  } else if (
    isInClinic(state, clinicTypes.HTN) &&
    !hasHTNFollowUpAppointment(state)
  ) {
    return $t('sideMenu.clinics.bookFollowup')
  } else {
    return $t('sideMenu.clinics.bookAppointment')
  }
}

export const mhSideMenuDisplay = state => {
  if (hasMHAppointment(state)) {
    return $t('sideMenu.clinics.nextAppointments')
  } else if (isReferredToClinic(state, clinicTypes.MENTAL)) {
    return $t('sideMenu.clinics.referral')
  } else if (
    isInClinic(state, clinicTypes.MENTAL) &&
    !hasMHAppointment(state)
  ) {
    return $t('sideMenu.clinics.bookFollowup')
  } else {
    return $t('sideMenu.clinics.bookAppointment')
  }
}
export const diabetesSideMenuDisplay = state => {
  if (hasDiabetesFollowUpAppointmentInNextWeek(state)) {
    return $t('sideMenu.followUpIntake')
  } else if (hasDiabetesAppointment(state)) {
    return $t('sideMenu.clinics.nextAppointments')
  } else if (isReferredToClinic(state, clinicTypes.DIABETES)) {
    return $t('sideMenu.clinics.referral')
  } else if (
    isInClinic(state, clinicTypes.DIABETES) &&
    !hasDiabetesFollowUpAppointment(state)
  ) {
    return $t('sideMenu.clinics.bookFollowup')
  } else {
    return $t('sideMenu.clinics.bookAppointment')
  }
}

export const clinicName = (antidote_clinic, isFollowUp = false) => {
  let clinicName = $t('clinicNames.primary')
  switch (antidote_clinic) {
    case clinicTypes.PRIMARY + HMOService: {
      clinicName = $t('clinicNames.primary')
      break
    }
    case clinicTypes.PRIMARY: {
      clinicName = isFollowUp
        ? $t('clinicNames.primaryFu')
        : $t('clinicNames.primary')
      break
    }
    case clinicTypes.MENTAL: {
      clinicName = isFollowUp
        ? $t('clinicNames.mentalFu')
        : $t('clinicNames.mental')
      break
    }

    case clinicTypes.HTN: {
      clinicName = isFollowUp
        ? $t('clinicNames.hypertensionFu')
        : $t('clinicNames.hypertension')
      break
    }

    case clinicTypes.DIABETES: {
      clinicName = isFollowUp
        ? $t('clinicNames.diabetesFu')
        : $t('clinicNames.diabetes')
      break
    }

    default:
      clinicName = isFollowUp
        ? $t('clinicNames.primaryFu')
        : $t('clinicNames.primary')
  }
  return clinicName
}
export const convertClinicNames = clinicType => {
  switch (clinicType) {
    case 'Hypertension': {
      return clinicTypes.HTN
    }
    case 'Diabetes': {
      return clinicTypes.DIABETES
    }
    case 'Mental Health': {
      return clinicTypes.MENTAL
    }
    default:
      return null
  }
}
export const shouldDisplayInitClinic = clinic => {
  return clinic && clinic !== clinicTypes.PRIMARY
}

export const getClinicAction = (
  antidote_clinic,
  actionKey,
  isFollowUp = false
) => {
  return isFollowUp
    ? clinicActions[antidote_clinic]?.[actionKey]?.followup
    : clinicActions[antidote_clinic]?.[actionKey]?.initial
}

export const isMhServiceSelected = state =>
  state.getCare.selectedService === clinicTypes.MENTAL
export const isMhServiceAvailable = state =>
  state.app.availableServices[clinicTypes.MENTAL]
export const isHtnServiceSelected = state =>
  state.getCare.selectedService === clinicTypes.HTN
export const isHtnServiceAvailable = state =>
  state.app.availableServices[clinicTypes.HTN]
export const isDmServiceSelected = state =>
  state.getCare.selectedService === clinicTypes.DIABETES
export const isDmServiceAvailable = state =>
  state.app.availableServices[clinicTypes.DIABETES]
export const isPrimaryServiceSelected = state =>
  !state.getCare.selectedService ||
  state.getCare.selectedService === clinicTypes.PRIMARY
export const isPrimaryServiceAvailable = state =>
  getIsHmoMember(state?.profile?.userDetails)
    ? // primaryHMO or primary
      state.app?.availableServices[clinicTypes.PRIMARY + HMOService]
    : state.app?.availableServices[clinicTypes.PRIMARY]
export const isRescheduleFlow = state => state.getCare?.isRescheduleFlow

export const getOriginAndVariantByClinic = (clinic, isFollowUp, isMinor) => {
  switch (clinic) {
    case clinicTypes.HTN: {
      return isFollowUp
        ? { origin: Origin.HTN_FU, variant: Variant.HTN_FU }
        : { origin: Origin.HTN, variant: Variant.HTN }
    }
    case clinicTypes.MENTAL: {
      return isFollowUp
        ? { origin: Origin.MENTAL_FU, variant: Variant.MENTAL_FU }
        : { origin: Origin.MENTAL, variant: Variant.MENTAL }
    }
    case clinicTypes.DIABETES: {
      return isFollowUp
        ? { origin: Origin.DIABETES_FU, variant: Variant.DIABETES_FU }
        : { origin: Origin.DIABETES, variant: Variant.DIABETES }
    }
    default: {
      return isMinor
        ? { origin: Origin.PEDIATRICK, variant: Variant.PEDIATRICK }
        : { origin: Origin.PRIMARY, variant: Variant.PRIMARY }
    }
  }
}

export const getOriginByIntent = (intent, hmoServices) => {
  // intent only presented for HMO user. HTN and DM clinics should show the hmo version
  // we are adding 'HMO' to origin to make sure the hmo version of those clinics will be presented in the bot
  const serviceClinic = hmoServices?.map(service => service.service_clinic)
  switch (intent) {
    case HMOIntentOptions.HTN: {
      return isFollowUp(clinicTypes.HTN, serviceClinic)
        ? Origin.HTN_FU + HMOService
        : Origin.HTN + HMOService
    }
    case HMOIntentOptions.DIABETES: {
      return isFollowUp(clinicTypes.DIABETES, serviceClinic)
        ? Origin.DIABETES_FU + HMOService
        : Origin.DIABETES + HMOService
    }
    default: {
      return intent
    }
  }
}
